import { GenericSelect } from '@/components/GenericSelect';
import BaseLayout from '@/layout/BaseLayout';
import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { trpc, trpcClient } from '@/trpcClient';
import { logger } from '@/utils/logger';
import { usePreventLeave } from '@/utils/usePreventLeave';
import { urlToFile } from '@/utils/utils';

/**
 * Convert a File to base64 string (without data URL prefix)
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
import { checkFacilityEditInput, type EditFacilityInputType } from '@shelflix/schemas/facility/edit';
import type { UserDto } from '@shelflix/trpc/routes/user/list/useCase';
import Dropzone from '@shelflix/ui-web/components/Dropzone/index';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useCepAutoFill } from '../FacilityCreate/store';
import { useFacilityFormStore } from './store';

export function FacilityEdit() {
  const { uuid: facilityUuid } = useParams<{ uuid: string }>();
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const uploadImageMutation = useMutation(trpc.image.uploadCompressed.mutationOptions());

  const migrateFacilityMutation = useMutation(trpc.migration.facilityMigration.mutationOptions());

  const { data: facilityToEdit, isLoading: _isLoadingBrand } = useQuery(
    trpc.facility.get.queryOptions(
      {
        uuid: facilityUuid ?? '',
      },
      {
        enabled: !!facilityUuid,
      }
    )
  );
  const { formData, resetForm } = useFacilityFormStore();

  const form = useForm<
    EditFacilityInputType & {
      responsibleUser: {
        uuid: string;
        name: string;
      };
    }
  >({
    defaultValues: {
      ...formData,
    },
    mode: 'all',
  });

  useCepAutoFill(form);

  useEffect(() => {
    if (facilityToEdit) {
      urlToFile(facilityToEdit.logoUrl, 'default').then((file) => {
        setThumbnail(file);

        form.reset({
          ...facilityToEdit,
          cityIbgeCode: facilityToEdit.cityIbgeCode,
          city: facilityToEdit.city,
          fileType: thumbnail?.type,
          referenceLocation: facilityToEdit.referenceLocation || undefined,
          complement: facilityToEdit.complement || undefined,
          responsibleUser: facilityToEdit.responsibleUser ?? undefined,
        });
      });
    }
  }, [facilityToEdit]);

  const editFacilityMutation = useMutation(trpc.facility.edit.mutationOptions());

  const navigate = useNavigate();

  const onSubmit = async (
    data: EditFacilityInputType & {
      responsibleUser: {
        uuid: string;
        name: string;
      };
    }
  ) => {
    if (editFacilityMutation.isPending || uploadImageMutation.isPending) return;

    try {
      setIsUploading(true);

      // First, upload the image with compression if there's a new thumbnail
      let imageKey: string | undefined;
      if (thumbnail) {
        const base64Data = await fileToBase64(thumbnail);
        const uploadResult = await uploadImageMutation.mutateAsync({
          base64Data,
          filename: thumbnail.name,
          imageType: 'facility-logo',
        });
        imageKey = uploadResult.key;
        logger.info('Image uploaded', {
          originalSize: uploadResult.originalSize,
          compressedSize: uploadResult.compressedSize,
          savingsPercent: uploadResult.savingsPercent,
        });
      }

      // Only send fields expected by the schema - exclude extra fields like logoUrl, material, active
      // Use conditional spreading for optional fields to avoid sending null values
      const payload: EditFacilityInputType = {
        uuid: data.uuid,
        responsibleUser: typeof data.responsibleUser === 'string' ? data.responsibleUser : data.responsibleUser.uuid,
        name: data.name,
        cnpj: data.cnpj,
        cep: data.cep,
        address: data.address,
        district: data.district,
        number: data.number,
        city: data.city,
        state: data.state,
        cityIbgeCode: data.cityIbgeCode,
        ...(imageKey && { imageKey }), // Use pre-uploaded key if available
        ...(data.complement && { complement: data.complement }),
        ...(data.referenceLocation && { referenceLocation: data.referenceLocation }),
        ...(data.latitude != null && { latitude: data.latitude }),
        ...(data.longitude != null && { longitude: data.longitude }),
      };

      await editFacilityMutation.mutateAsync(payload);
      resetForm();
      await navigate('/facility', { state: { toastSuccess: true } });
    } catch (error) {
      toast.error(`Erro ao editar estabelecimento: ${(error as Error).message}`);
    } finally {
      setIsUploading(false);
    }
  };

  usePreventLeave(isUploading);

  const dropzoneRef = useRef<HTMLDivElement>(null);

  return (
    <BaseLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="w-full">
          <div className="flex w-full flex-col gap-4">
            <section className="w-full overflow-hidden rounded-[10px]">
              <Card className="w-full rounded-[10px]">
                <CardHeader className="pb-0">
                  <CardTitle className="text-primary text-[18px] leading-5 font-semibold sm:text-2xl">
                    Informações do Estabelecimento
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex flex-col gap-[28px] pt-4 sm:flex-row">
                  <div className="flex flex-col justify-between sm:w-1/4">
                    <FormField
                      control={form.control}
                      name="fileType"
                      rules={{ validate: (_) => checkFacilityEditInput('fileType', thumbnail?.type) }}
                      render={({ field }) => (
                        <FormItem className="flex flex-col gap-[3px]">
                          <FormControl>
                            <Dropzone
                              removable={false}
                              onFilesSelected={(files) => {
                                setThumbnail(files[0]);
                                field.onChange(files[0]?.type);
                              }}
                              ref={dropzoneRef}
                              title="Arraste ou clique aqui para selecionar seu arquivo"
                              description="JPG, PNG ou SVG com fundo transparente (16:9)"
                              value={thumbnail ? [thumbnail] : undefined}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex flex-col gap-5 sm:w-3/4">
                    <div className="flex w-full flex-col gap-4 sm:flex-row">
                      <div className="sm:w-2/3">
                        <FormField
                          control={form.control}
                          name="name"
                          rules={{ validate: (value) => checkFacilityEditInput('name', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                Nome do Estabelecimento
                              </FormLabel>
                              <FormControl>
                                <Input className="h-10" placeholder="Digite o nome do estabelecimento" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="sm:w-1/3">
                        <FormField
                          control={form.control}
                          name="cnpj"
                          rules={{ validate: (value) => checkFacilityEditInput('cnpj', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">CNPJ</FormLabel>
                              <FormControl>
                                <Input
                                  mask="99.999.999/9999-99"
                                  className="h-10"
                                  placeholder="00.000.000/0000-00"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="flex w-full flex-col gap-4 sm:flex-row">
                      <div className="sm:w-1/3">
                        <FormField
                          control={form.control}
                          name="cep"
                          rules={{ validate: (value) => checkFacilityEditInput('cep', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">CEP</FormLabel>
                              <FormControl>
                                <Input mask="99999-999" className="h-10" placeholder="00000-000" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="sm:w-1/3">
                        <FormField
                          control={form.control}
                          name="address"
                          rules={{ validate: (value) => checkFacilityEditInput('address', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                Logradouro
                              </FormLabel>
                              <FormControl>
                                <Input className="h-10" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="sm:w-1/3">
                        <FormField
                          control={form.control}
                          name="district"
                          rules={{ validate: (value) => checkFacilityEditInput('district', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Bairro</FormLabel>
                              <FormControl>
                                <Input className="h-10" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="flex w-full flex-col gap-4 sm:flex-row">
                      <div className="sm:basis-1/6">
                        <FormField
                          control={form.control}
                          name="number"
                          rules={{ validate: (value) => checkFacilityEditInput('number', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Número</FormLabel>
                              <FormControl>
                                <Input className="h-10" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="sm:basis-1/3">
                        <FormField
                          control={form.control}
                          name="city"
                          rules={{ validate: (value) => checkFacilityEditInput('city', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Cidade</FormLabel>
                              <FormControl>
                                <Input className="h-10" disabled {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="grow-1 sm:basis-1/6">
                        <FormField
                          control={form.control}
                          name="state"
                          rules={{ validate: (value) => checkFacilityEditInput('state', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">UF</FormLabel>
                              <Select value={field.value} onValueChange={field.onChange} disabled>
                                <FormControl>
                                  <SelectTrigger className="flex !h-10 w-full items-center rounded-md border px-3 text-sm">
                                    <SelectValue placeholder="Selecione a UF" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className=" ">
                                  {[
                                    'AC',
                                    'AL',
                                    'AP',
                                    'AM',
                                    'BA',
                                    'CE',
                                    'DF',
                                    'ES',
                                    'GO',
                                    'MA',
                                    'MT',
                                    'MS',
                                    'MG',
                                    'PA',
                                    'PB',
                                    'PR',
                                    'PE',
                                    'PI',
                                    'RJ',
                                    'RN',
                                    'RS',
                                    'RO',
                                    'RR',
                                    'SC',
                                    'SP',
                                    'SE',
                                    'TO',
                                  ].map((uf) => (
                                    <SelectItem key={uf} value={uf}>
                                      {uf}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="sm:basis-1/3">
                        <FormField
                          control={form.control}
                          name="complement"
                          rules={{ validate: (value) => checkFacilityEditInput('complement', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                Complemento
                              </FormLabel>
                              <FormControl>
                                <Input className="h-10" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                    <div className="flex flex-row gap-4">
                      <FormField
                        control={form.control}
                        name="latitude"
                        render={({ field }) => (
                          <FormItem className="flex flex-col gap-[3px]">
                            <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                              Latitude (opcional)
                            </FormLabel>
                            <FormControl>
                              <Input type="number" step="any" placeholder="Latitude" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="longitude"
                        render={({ field }) => (
                          <FormItem className="flex flex-col gap-[3px]">
                            <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                              Longitude (opcional)
                            </FormLabel>
                            <FormControl>
                              <Input type="number" step="any" placeholder="Longitude" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="sm:w-3/4">
                      <FormField
                        control={form.control}
                        name="referenceLocation"
                        rules={{ validate: (value) => checkFacilityEditInput('referenceLocation', value) }}
                        render={({ field }) => (
                          <FormItem className="flex flex-col gap-[3px]">
                            <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                              Ponto de Referência
                            </FormLabel>
                            <FormControl>
                              <Input className="h-10" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            <Card className="rounded-[5px]">
              <CardContent>
                <div className="mb-6">
                  <CardTitle className="text-primary mb-2 [font-family:'Poppins'] text-[18px] leading-5 font-semibold sm:text-2xl">
                    Promotor responsável
                  </CardTitle>
                  <p className="[font-family:'Figtree'] text-sm leading-5 text-[#2d2d2d]">
                    Selecione um usuário promotor, para ser responsável pelo estabelecimento, através do nome ou e-mail.
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name="responsibleUser"
                  rules={{ validate: (value) => checkFacilityEditInput('responsibleUser', value?.uuid ?? value) }}
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-[3px] sm:w-[391px]">
                      <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                        Pesquisar usuário
                      </FormLabel>
                      <FormControl>
                        <div className="flex flex-row items-center gap-2">
                          <GenericSelect<UserDto>
                            onSelect={(user) => field.onChange(user.uuid ?? user)}
                            value={field.value}
                            fetchItems={async (search) => {
                              if (!search) return [];

                              // Fetch directly via trpcClient to avoid stale data from useQuery cache
                              const result = await trpcClient.user.list.query({
                                search: {
                                  fields: ['name', 'email'],
                                  value: search,
                                },
                                filter: [
                                  { name: 'hierarchy', value: undefined, condition: 'min' },
                                  { name: 'active', condition: 'equals', value: true },
                                ],
                              });

                              return result.users ?? [];
                            }}
                            renderItem={(user) => (
                              <div>
                                <div className="text-sm font-medium">{user.name}</div>
                                <div className="text-muted-foreground text-xs">{user.email}</div>
                              </div>
                            )}
                            getItemLabel={(user) => user.name}
                            getItemKey={(user) => user.uuid}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <div className="my-t flex grow-0 justify-center">
              <RestrictedComponent
                requiredScopes={['admin:facilities:edit']}
                behavior="disable"
                showToast
                toastMessage="Você não tem permissão para editar estabelecimentos."
              >
                <Button
                  loading={editFacilityMutation.isPending || isUploading}
                  type="submit"
                  className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-auto sm:w-fit sm:grow-0"
                >
                  <Icon name="label" fill={1} color="#fff" size={16} />
                  <span className="[font-family:'Figtree'] text-sm">Salvar estabelecimento</span>
                </Button>
              </RestrictedComponent>

              {facilityToEdit?.externalId === null && (
                <Button
                  disabled={migrateFacilityMutation.isPending}
                  className="bg-primary hover:bg-primary ml-4 flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-fit sm:grow-0"
                  onClick={(e) => {
                    e.preventDefault();
                    migrateFacilityMutation.mutate(
                      {
                        facilityUuid: facilityUuid || '',
                      },
                      {
                        onSuccess: () => {
                          toast.success('Migração realizada com sucesso');
                        },
                        onError: (error) => {
                          toast.error(`Erro ao realizar migração: ${error.message}`);
                        },
                      }
                    );
                  }}
                >
                  <span className="font-['Figtree'] text-sm">Migrar Estabelecimento</span>
                </Button>
              )}
            </div>
          </div>
        </form>
      </Form>
    </BaseLayout>
  );
}
